import { HttpResponse } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { saveAs } from "file-saver";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { OffersService } from "../offers.service";
import { CloneOffer, ExportOffers, GetNewOfferNumber, GetOfferComponentsById, GetOfferCustomers, GetOfferOrigins, GetOfferStatuses, GetOfferTerms, GetTobaccoType, GetTobaccoTypes, IsOfferComponentEditable, IsOfferEditable, OnActionOffer, OnGetOffers, OnRemoveOffer, OnRemoveOfferComponent, OnSetCurrentOrderRenderer, OnSetNewOfferComponent, OnSetOfferStatus, SaveOffer, SetIsNew, SetOffersComponentsGridConfig, SetOffersFormGridConfig, SetSelectedOffer, SetSelectedOfferComponent, SetSelectedOfferGridData, UpdatedOfferWebhook } from "./offers.action";
import { OffersStateModel, Offer, OfferComponent } from "./offers.model";

import { tap, catchError } from "rxjs/operators";
import { SharedGridState } from "app/modules/shared/ag-grid/shared-grid-state/shared-grid.state";
import { produce } from "immer";
import { OnCanCloseDialog, OnSetSharedDropdowns, SetShowLoader, ShowToastrAlert } from "app/modules/shared-state/shared.actions";
import { OnRemoveSharedGridRowData, OnSetSharedGridConfig, OnSetSharedGridRowData, SetCanRowDrag } from "app/modules/shared/ag-grid/shared-grid-state/shared-grid.actions";
import { UpdateFormValue } from "@ngxs/form-plugin";
import { MatDialog } from "@angular/material/dialog";
import { DropdownComponent } from "app/modules/shared/dropdown/dropdown.component";
import { SharedState } from "app/modules/shared-state/shared.state";
import { FuseConfirmationService } from "@fuse/services/confirmation";
import { firstValueFrom } from "rxjs";
import { DropdownOption } from "app/modules/purchase-confirmations/purchase-confirmations-state/purchase-confirmations.model";
import { Router } from "@angular/router";
import { resolveExportHttpErrorMessage } from "app/modules/shared/http-error-message.util";

@State<OffersStateModel>({
    name: 'offersstate',
    defaults: {
        offers: [],
        offerComponents: [],
        error: null,

        offersForm: {
            model: {
                _id: '',
                title: '',
                offerNumber: '',
                customer: '',
                offerDate: '',
                valueDate: '',
                attention: '',
                status: '',
                customerId: '67af00fd5ea1cefe4ae1537d',
                offerComponents: []
            },
            dirty: false,
            status: '',
            errors: null,
        },

        offersComponentForm: {
            model: {
                offerGrade: '',
                internalGrade: '',
                tobaccoForm: '',
                tobaccoType: '',
                nicotine: null,
                sugar: null,
                cropYear: null,
                cartonNett: null,
                nettKgs: null,
                usdPerKg: null,
            },
            dirty: false,
            status: '',
            errors: null,
        },

        offerTermsForm: {
            model: null,
            dirty: false,
            status: '',
            errors: null,
        },

        isNew: false,
        selectedOffer: { _id: null, md5Checksum: null },
        isOfferEditable: false,
        isOfferBeingCloned: false,
        offerCustomers: [],
        isOfferComponentEditable: false,
        selectedGridData: { _id: null, md5Checksum: null },
        tobaccoTypes: [],
        tobaccoType: [],
        offerOrigins: [],
        clonedComponents: [],
        currentRendererId: null,
        newOfferNumber: { offerNumber: null },
        selectedOfferComponent: null,
        editedRowIndex: null,
    }
})

@Injectable()
export class OffersState {

    private _offersService = inject(OffersService);
    private _store = inject(Store);
    private _router = inject(Router);

    @Selector()
    static getError(state: OffersStateModel) {
        return state.error;
    }

    @Action(OnGetOffers)
    onGetOffers(ctx: StateContext<OffersStateModel>, { payload }: OnGetOffers) {
        ctx.dispatch(new SetShowLoader(true));
        ctx.patchState({ error: null });


        return this._offersService.getOffers().pipe(
            catchError((error) => {
                ctx.patchState({ error: error.message });

                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Failed to load offers: ${error.message}`,
                    position: 'toast-top-center'
                }));

                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((offers: Offer[]) => {

                ctx.dispatch(new SetShowLoader(false));
                ctx.patchState({ offers });

                ctx.dispatch(new OnSetSharedGridRowData({
                    reload: true,
                    payload: offers,
                    gridUUID: payload.gridUUID
                }));
            })
        );
    }

    @Action(ExportOffers)
    exportOffers(ctx: StateContext<OffersStateModel>) {
        ctx.dispatch(new SetShowLoader(true));
        return this._offersService.exportOffers().pipe(
            catchError((error) => {
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: "error",
                    messageTitle: "Error",
                    messageBody: resolveExportHttpErrorMessage(error, "Could not export offers."),
                    position: "toast-top-center"
                }));
                throw error;
            }),
            tap((response: HttpResponse<Blob>) => {
                const blob = response.body;
                if (!blob) {
                    ctx.dispatch(new SetShowLoader(false));
                    return;
                }
                const header = response.headers.get("Content-Disposition");
                let filename = `offers-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
                if (header) {
                    const match = /filename="([^"]+)"/.exec(header) ?? /filename=([^;\s]+)/.exec(header);
                    if (match) {
                        filename = match[1];
                    }
                }
                saveAs(blob, filename);
                ctx.dispatch(new SetShowLoader(false));
            })
        );
    }

    @Action(GetNewOfferNumber)
    getNewOfferNumber(ctx: StateContext<OffersStateModel>) {
        return this._offersService.getNewOfferNumber().pipe(
            catchError((error) => {
                ctx.patchState({ error: error.message });

                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Failed to load offers: ${error.message}`,
                    position: 'toast-top-center'
                }));

                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((offerNumber: { offerNumber: string; }) => {
                ctx.dispatch(new UpdateFormValue({
                    path: 'offersstate.offersForm',
                    value: { offerNumber: offerNumber.offerNumber }
                }));
                ctx.patchState({ newOfferNumber: { offerNumber: offerNumber.offerNumber } });
            })
        );
    }

    @Selector()
    static getNewOfferNumber(state: OffersStateModel) {
        return state.newOfferNumber;
    }

    @Action(SetOffersFormGridConfig)
    setOffersFormGridConfig(ctx: StateContext<OffersStateModel>, { payload }: SetOffersFormGridConfig) {
        const store = inject(Store);
        const parentGridConfig = store.selectSnapshot(SharedGridState.getGridConfig);
        const thirdGridUUID = store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).thirdGridUUID;

        const selectedOffer = ctx.getState().selectedOffer._id;
        if (selectedOffer) {

            return this._offersService.getOfferById(selectedOffer).pipe(
                catchError((error) => {
                    ctx.dispatch(new ShowToastrAlert({
                        showToast: true,
                        toastrType: 'error',
                        messageTitle: 'Error',
                        messageBody: `Error saving offer: ${error.error.message.message}`,
                        position: 'toast-top-center'
                    }));
                    ctx.dispatch(new SetShowLoader(false));
                    throw error;
                }),
                tap((offer: Offer) => {

                    const updatedGridConfig = produce(parentGridConfig, (draft) => {
                        draft[thirdGridUUID] = payload.thirdGridConfig[thirdGridUUID];
                        draft[thirdGridUUID].rowData = offer.offerComponents;
                    });

                    ctx.dispatch(new OnSetSharedGridConfig(updatedGridConfig));
                }),

            );
        } else {
            const updatedGridConfig = produce(parentGridConfig, (draft) => {
                draft[thirdGridUUID] = payload.thirdGridConfig[thirdGridUUID];
            });

            ctx.dispatch(new OnSetSharedGridConfig(updatedGridConfig));
        }
    }



    @Action(SaveOffer)
    saveOffer(ctx: StateContext<OffersStateModel>) {

        ctx.dispatch(new SetShowLoader(true));
        const formData = ctx.getState().offersForm;
        const offerTermsFormData = ctx.getState().offerTermsForm;
        const gridReferenceUUIDs = this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs);
        const offersComponentData = this._store.selectSnapshot(SharedGridState.getGridConfig)[gridReferenceUUIDs.thirdGridUUID]?.rowData || [];

        if (offersComponentData.length === 0) {
            ctx.dispatch(new ShowToastrAlert({
                showToast: true,
                toastrType: 'error',
                messageTitle: 'Error',
                messageBody: 'You must add at least one offer component.',
                position: 'toast-top-center'
            }));
            ctx.dispatch(new SetShowLoader(false));
            return;
        }

        const payload = {
            ...formData.model,
            status: 'PENDING',
            ...offerTermsFormData.model,
            offerComponents: offersComponentData
        };

        return this._offersService.createOffer(payload).pipe(
            catchError((error) => {
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Error saving offer: ${error.error.message.message}`,
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new SetShowLoader(false));
                return error;
            }),
            tap((newOffer) => {
                ctx.dispatch(new OnSetSharedGridRowData({
                    payload: [newOffer],
                    gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID
                }));
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new OnCanCloseDialog(true));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Success',
                    messageBody: `offer created for ${newOffer.offerNumber}`,
                    position: 'toast-top-center'
                }));
            })
        );
    }


    @Action(OnSetNewOfferComponent)
    onSetNewOfferComponent(ctx: StateContext<OffersStateModel>) {
        const componentFormData = ctx.getState().offersComponentForm.model;
        const state = ctx.getState();
        const payload = state.offersComponentForm.model;
        const isEditable = state.isOfferComponentEditable;
        const gridConfig = this._store.selectSnapshot(SharedGridState.getGridConfig);
        const gridUUIDs = this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs);
        const rowData = gridConfig[gridUUIDs.thirdGridUUID]?.rowData || [];
        const selectedGridData = state.selectedGridData;
        const selectedOfferComponent = state.selectedOfferComponent;
        const editedRowIndex = state.editedRowIndex;

        const sortOrder = (rowData.length > 0) ? rowData[rowData.length - 1].sortOrder + 1 : 1;

        if (isEditable) {
            if (selectedOfferComponent._id) {
                const updatedRowData = produce(rowData, draft => {
                    const index = draft.findIndex(component => component._id === selectedGridData._id);
                    if (index !== -1) {
                        draft[index] = { ...draft[index], ...payload, edited: true };
                    }
                });

                ctx.patchState({ offerComponents: updatedRowData });
                ctx.dispatch(new OnSetSharedGridRowData({
                    update: true,
                    payload: [updatedRowData.find(component => component._id === selectedGridData._id)],
                    gridUUID: gridUUIDs.thirdGridUUID
                }));

                ctx.dispatch(new SetShowLoader(false));
                return;
            } else {
                const updatedRowData = produce(rowData, draft => {
                    draft[editedRowIndex] = { ...payload, edited: true };
                });
                ctx.dispatch(new OnSetSharedGridRowData({
                    reload: true,
                    payload: updatedRowData,
                    gridUUID: gridUUIDs.thirdGridUUID
                }));
                ctx.dispatch(new SetSelectedOfferComponent(null));
                ctx.dispatch(new SetSelectedOfferComponent(null));
                return;
            }
        }

        const updatedOfferComponents = produce(rowData, draft => {
            componentFormData['new'] = true;
            componentFormData['sortOrder'] = sortOrder;
            draft.push(componentFormData);
        });

        ctx.patchState({ offerComponents: updatedOfferComponents });

        ctx.dispatch(new OnSetSharedGridRowData({
            payload: [componentFormData],
            gridUUID: gridUUIDs.thirdGridUUID
        }));
    }

    @Action(SetSelectedOfferComponent)
    setSelectedInternalSalesProduct(ctx: StateContext<OffersStateModel>, { payload }: SetSelectedOfferComponent) {

        let editedRowIndex = null;
        if (payload) {
            const rowData = this._store.selectSnapshot(SharedGridState.getGridConfig)[this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).thirdGridUUID].rowData;
            editedRowIndex = rowData.findIndex(row => row.offerGrade === payload.offerGrade
                && row.tobaccoForm === payload.tobaccoForm
                && row.cropYear === payload.cropYear
                && row.internalGrade === payload.internalGrade
                && row.nicotine === payload.nicotine
                && row.sugar === payload.sugar);
        }
        ctx.patchState({
            selectedOfferComponent: payload,
            editedRowIndex: editedRowIndex
        });
    }



    @Action(SetSelectedOffer)
    setSelectedOffer(ctx: StateContext<OffersStateModel>, { payload }: SetSelectedOffer) {
        ctx.patchState({
            selectedOffer: {
                _id: payload._id,
                md5Checksum: payload.md5Checksum,
            },
            isOfferBeingCloned: payload.clone || false
        });
    }

    @Selector()
    static getSelectedOffer(state: OffersStateModel) {
        return state.selectedOffer;
    }

    @Selector()
    static getIsOfferBeingCloned(state: OffersStateModel) {
        return state.isOfferBeingCloned;
    }

    @Action(SetSelectedOfferGridData)
    setSelectedOfferGridData(ctx: StateContext<OffersStateModel>, { payload }: SetSelectedOfferGridData) {
        ctx.patchState({
            selectedGridData: {
                _id: payload._id,
                md5Checksum: payload.md5Checksum
            },
        });
    }

    @Selector()
    static getSelectedOfferGridData(state: OffersStateModel) {
        return state.selectedOffer;
    }

    @Action(IsOfferEditable)
    isOfferEditable(ctx: StateContext<OffersStateModel>, { payload }: IsOfferEditable) {
        ctx.patchState({ isOfferEditable: payload });
    }
    @Action(IsOfferComponentEditable)
    isOfferComponentEditable(ctx: StateContext<OffersStateModel>, { payload }: IsOfferComponentEditable) {
        ctx.dispatch(new SetCanRowDrag(payload));
        ctx.patchState({ isOfferComponentEditable: payload });
    }


    @Selector()
    static getIsOfferEditable(state: OffersStateModel) {
        return state.isOfferEditable;
    }

    @Selector()
    static getIsOfferComponentEditable(state: OffersStateModel) {
        return state.isOfferComponentEditable;
    }

    @Action(OnActionOffer)
    onActionOffer(ctx: StateContext<OffersStateModel>) {
        ctx.dispatch(new SetShowLoader(true));


        const selectedOffer = ctx.getState().selectedOffer;
        const offersForm = ctx.getState().offersForm;
        const offerTermsFormData = ctx.getState().offerTermsForm;
        //const updatedOfferComponents = ctx.getState().offerComponents;

        const gridReferenceUUIDs = this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs);
        const offersComponentData = this._store.selectSnapshot(SharedGridState.getGridConfig)[gridReferenceUUIDs.thirdGridUUID]?.rowData || [];
        if (offersComponentData.length === 0) {
            ctx.dispatch(new ShowToastrAlert({
                showToast: true,
                toastrType: 'error',
                messageTitle: 'Error',
                messageBody: 'You must add at least one offer component.',
                position: 'toast-top-center'
            }));
            ctx.dispatch(new SetShowLoader(false));
            return;
        }
        const cleanedComponents = offersComponentData.map(
            ({
                _id,
                offerGrade,
                internalGrade,
                tobaccoType,
                tobaccoForm,
                nicotine,
                sugar,
                cropYear,
                cartonNett,
                nettKgs,
                usdPerKg,
                offerId,
                sortOrder,
            }) => ({
                _id,
                offerGrade,
                internalGrade,
                tobaccoType,
                tobaccoForm,
                nicotine,
                sugar,
                cropYear,
                cartonNett,
                nettKgs,
                usdPerKg,
                offerId,
                sortOrder,
            })
        );



        return this.onUpdateOffer(ctx, {
            _id: selectedOffer._id,
            ...offersForm.model,
            ...offerTermsFormData.model,
            offerComponents: cleanedComponents,
        }, selectedOffer.md5Checksum);

    }

    async onUpdateOffer(ctx: StateContext<OffersStateModel>, payload: Offer, md5Checksum: string) {
        return this._offersService.updateOffer(payload._id as string, payload, md5Checksum).pipe(
            catchError((error) => {
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Error updating offer: ${error.error.message.message}`,
                    position: 'toast-top-center'
                }));
                throw error;
            }),

            tap((updatedOffer: Offer) => {
                ctx.dispatch(new OnSetSharedGridRowData({
                    update: true,
                    payload: [updatedOffer],
                    gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID
                }));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Success',
                    messageBody: 'Offer updated successfully',
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new OnCanCloseDialog(true));
            })
        );
    }


    @Action(CloneOffer)
    cloneOffer(ctx: StateContext<OffersStateModel>, { payload }: CloneOffer) {
        // const gridReferenceUUIDs = this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs);

        this._store.dispatch(new GetNewOfferNumber());

        const clonedOffer: Offer = {
            ...payload,
            _id: '',
            title: `${payload.title} (Copy)`,
            status: 'PENDING',
            offerComponents: payload.offerComponents.map(component => ({
                ...component,
                _id: undefined,
            })),
        };

        ctx.patchState({
            selectedOffer: { _id: null, md5Checksum: null },
            clonedComponents: clonedOffer.offerComponents,
        });

        ctx.dispatch(new UpdateFormValue({
            path: 'offersstate.offersForm',
            value: clonedOffer
        }));

    }

    @Selector()
    static getClonedComponents(state: OffersStateModel) {
        return state.clonedComponents;
    }

    @Action(OnRemoveOffer)
    onRemoveOffer(ctx: StateContext<OffersStateModel>, { payload }: OnRemoveOffer) {

        ctx.dispatch(new SetShowLoader(true));

        return this._offersService.deleteOffer(payload.offerId).pipe(
            catchError((error) => {
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Failed to remove offer: ${error.message}`,
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap(() => {
                ctx.dispatch(new OnRemoveSharedGridRowData({
                    gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID,
                    removeRow: { _id: payload.offerId }
                }));

                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Offer Removed',
                    messageBody: `Offer successfully removed.`,
                    position: 'toast-top-center'
                }));

                ctx.dispatch(new SetShowLoader(false));
            }),

        );
    }

    @Action(OnRemoveOfferComponent)
    onRemoveOfferComponent(ctx: StateContext<OffersStateModel>, { payload }: OnRemoveOfferComponent) {
        const gridConfig = this._store.selectSnapshot(SharedGridState.getGridConfig);
        const gridUUIDs = this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs);

        if (ctx.getState().isOfferComponentEditable) {
            ctx.dispatch(new ShowToastrAlert({
                showToast: true,
                toastrType: 'error',
                messageTitle: 'Error',
                messageBody: 'You cannot delete a sales confirmation component when editing a shipping component.',
                position: 'toast-top-center'
            }));
            return;
        }
        const rowData = gridConfig[gridUUIDs.thirdGridUUID].rowData;

        const updatedRowData = produce(rowData, draft => {
            if (payload.offerComponentId) {
                for (const offerRow of draft) {
                    if (offerRow._id === payload.offerComponentId) {
                        if (!offerRow.edited || ctx.getState().isOfferBeingCloned) {
                            draft.splice(draft.indexOf(offerRow), 1);
                        } else {
                            ctx.dispatch(new ShowToastrAlert({
                                showToast: true,
                                toastrType: 'error',
                                messageTitle: 'Error',
                                messageBody: 'You cannot delete an offer component that has been edited.',
                                position: 'toast-top-center'
                            }));
                            throw new Error('You cannot delete an offer component that has been edited.');
                        }
                    }
                }
            } else if (payload.offerComponent) {
                const index = draft.findIndex(offerRow => {
                    return (offerRow.offerGrade === payload.offerComponent.offerGrade) &&
                        (offerRow.internalGrade === payload.offerComponent.internalGrade) &&
                        (offerRow.tobaccoForm === payload.offerComponent.tobaccoForm) &&
                        (offerRow.tobaccoType === payload.offerComponent.tobaccoType) &&
                        (offerRow.cropYear === payload.offerComponent.cropYear) &&
                        (offerRow.cartonNett === payload.offerComponent.cartonNett) &&
                        (offerRow.nettKgs === payload.offerComponent.nettKgs);
                });
                if (index !== -1) {
                    draft.splice(index, 1);
                }
            }
        });

        if (payload.offerComponentId && !ctx.getState().isOfferBeingCloned) {
            const _fuseConfirmationService = inject(FuseConfirmationService);
            const dialogRef = _fuseConfirmationService.open({
                title: 'Delete Offer Component',
                message: `Are you sure you want to delete this offer component? <br>
                    <strong>This action cannot be undone.</strong>`,
                icon: {
                    show: true,
                    color: 'primary'
                },
                actions: {
                    confirm: {
                        color: 'accent'
                    },
                },
                dismissible: false
            });
            dialogRef.afterClosed().subscribe(async (result) => {
                if (result === 'confirmed') {
                    await firstValueFrom(this._offersService.removeOfferComponent(payload.offerComponentId));
                    ctx.dispatch(new OnSetSharedGridRowData({
                        reload: true,
                        payload: updatedRowData,
                        gridUUID: gridUUIDs.thirdGridUUID
                    }));
                }
            });
        } else {
            ctx.dispatch(new OnSetSharedGridRowData({
                reload: true,
                payload: updatedRowData,
                gridUUID: gridUUIDs.thirdGridUUID
            }));
        }
    }



    @Action(GetOfferCustomers)
    getOfferCustomers(ctx: StateContext<OffersStateModel>) {
        ctx.patchState({ error: null });

        return this._offersService.getOfferCustomers().pipe(
            catchError((error) => {
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Failed to remove offer: ${error.message}`,
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new SetShowLoader(false));
                ctx.patchState({ error: error.message });
                return error;
            }),
            tap((customers: { _id: string; name: string; }[]) => {
                ctx.patchState({ offerCustomers: customers });
            }),
        );
    }

    @Selector()
    static getOfferCustomers(state: OffersStateModel) {
        return state.offerCustomers;
    }

    @Action(GetOfferTerms)
    getPurchaseOfferTerms(ctx: StateContext<OffersStateModel>, { payload }: GetOfferTerms) {
        return this._offersService.getOfferTerms(payload).pipe(
            catchError((error) => {
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Error fetching purchase confirmations: ${error.error.message.message}`,
                    position: 'toast-top-center'
                }));
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((terms: any) => {
                const doc = terms.reduce((acc: any, { key, value }) => {
                    acc[key] = value;
                    return acc;
                }, {});
                ctx.dispatch(new UpdateFormValue({
                    path: 'offersstate.offerTermsForm',
                    value: doc
                }));
            })
        );
    }

    @Action(SetIsNew)
    setIsNew(ctx: StateContext<OffersStateModel>, { payload }: SetIsNew) {
        ctx.patchState({ isNew: payload });
    }

    @Selector()
    static getIsNew(state: OffersStateModel) {
        return state.isNew;
    }


    @Action(GetOfferStatuses)
    getSalesShippingInstructionsStatuses(ctx: StateContext<OffersStateModel>) {
        const dialogRef = inject(MatDialog);
        return this._offersService.getDropdownsFilter({ types: ['offerStatuses'] }).pipe(
            catchError((error) => {
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: error.error.message.message,
                    position: 'toast-top-center'
                }));
                throw error;
            }),
            tap((response: { offerStatuses: string[]; }) => {
                ctx.dispatch(new OnSetSharedDropdowns(response.offerStatuses));
                const _dialog = dialogRef.open(DropdownComponent, {
                    width: '320px',
                    data: {
                        header: 'Change Status',
                        body: 'Please select the new status for the offer',
                    }
                });
                _dialog.afterClosed().subscribe((result) => {
                    if (!result) return;
                    this._store.dispatch(new UpdateFormValue({
                        path: 'shared.statusForm',
                        value: {
                            status: result
                        }
                    }));
                    ctx.dispatch(new OnSetOfferStatus());
                });
            })
        );
    }

    @Action(OnSetOfferStatus)
    OnSetOfferStatus(ctx: StateContext<OffersStateModel>) {
        const dropdownStatus = this._store.selectSnapshot(SharedState.getStatusForm).model.status;
        ctx.dispatch(new SetShowLoader(true));
        const selectedOffer = ctx.getState().selectedOffer;
        return this._offersService.updateOfferStatus(
            selectedOffer._id,
            dropdownStatus
        ).pipe(
            //TODO: Add an interface for the response that is generic for this return as its used all over
            catchError((error) => {
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: error.error.message.message,
                    position: 'toast-top-center'
                }));
                throw error;
            }),
            tap(() => {
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(new SetSelectedOffer({ _id: null, md5Checksum: null }));
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'success',
                    messageTitle: 'Success',
                    messageBody: 'Offer status updated successfully',
                    position: 'toast-top-center'
                }));

            })
        );
    }

    @Action(UpdatedOfferWebhook)
    updatedOfferWebhook(ctx: StateContext<OffersStateModel>, { payload }: UpdatedOfferWebhook) {
        const isOnOffersScreen = this._router.url.includes('offers');
        if (payload && isOnOffersScreen) {
            ctx.dispatch(new OnGetOffers({
                gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID
            }));
        }
    }

    @Selector()
    static getTobaccoTypes(state: OffersStateModel) {
        return state.tobaccoTypes;
    }

    @Action(GetTobaccoTypes)
    getTobaccoTypes(ctx: StateContext<OffersStateModel>) {

        return this._offersService.getTobaccoTypes().pipe(
            tap((tobaccoTypes: any[]) => {
                const formattedTobaccoTypes: DropdownOption[] = tobaccoTypes.map(item => ({
                    id: item.id?.toString() ?? '',
                    value: item.value ?? ''
                }));

                ctx.patchState({ tobaccoTypes: formattedTobaccoTypes });
            }),

            catchError((error) => {
                ctx.patchState({ error: error.message });

                this._store.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Failed to load tobacco types: ${error.message}`,
                    position: 'toast-top-center'
                }));

                return [];
            })
        );
    }

    @Selector()
    static getTobaccoType(state: OffersStateModel) {
        return state.tobaccoType;
    }

    @Action(GetOfferOrigins)
    getOfferOrigins(ctx: StateContext<OffersStateModel>) {

        return this._offersService.getOfferOrigins().pipe(
            tap((offerOrigins: any[]) => {
                const formattedOfferOrigins: DropdownOption[] = offerOrigins.map(item => ({
                    id: item.id?.toString() ?? '',
                    value: item.value ?? ''
                }));

                ctx.patchState({ offerOrigins: formattedOfferOrigins });
            }),

            catchError((error) => {
                ctx.patchState({ error: error.message });

                this._store.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Failed to load offer origins: ${error.message}`,
                    position: 'toast-top-center'
                }));

                return [];
            })
        );
    }

    @Selector()
    static getOfferOrigins(state: OffersStateModel) {
        return state.offerOrigins;
    }

    @Action(GetTobaccoType)
    getTobaccoType(ctx: StateContext<OffersStateModel>) {

        return this._offersService.getTobaccoType().pipe(
            tap((tobaccoTypes: any[]) => {
                const formattedTobaccoTypes: DropdownOption[] = tobaccoTypes.map(item => ({
                    id: item.id?.toString() ?? '',
                    value: item.value ?? ''
                }));

                ctx.patchState({ tobaccoType: formattedTobaccoTypes });
            }),

            catchError((error) => {
                ctx.patchState({ error: error.message });

                this._store.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Failed to load tobacco types: ${error.message}`,
                    position: 'toast-top-center'
                }));

                return [];
            })
        );
    }

    //** Cell Renderer */
    @Action(SetOffersComponentsGridConfig)
    setOffersComponentsGridConfig(ctx: StateContext<OffersStateModel>, { payload }: SetOffersComponentsGridConfig) {

        const store = inject(Store);
        const parentGridConfig = store.selectSnapshot(SharedGridState.getGridConfig);

        const updatedGridConfig = produce(parentGridConfig, (draft) => {

            draft[payload.gridUUID] = payload.gridConfig[payload.gridUUID];
        });
        ctx.dispatch(new OnSetSharedGridConfig(updatedGridConfig));

    }

    @Action(OnSetCurrentOrderRenderer)
    onSetCurrentRenderer(ctx: StateContext<OffersStateModel>, { payload }: OnSetCurrentOrderRenderer) {
        ctx.patchState({
            currentRendererId: payload.currentRenderer
        });
    }

    @Action(GetOfferComponentsById)
    getOfferComponentsById(ctx: StateContext<OffersStateModel>, { payload }: GetOfferComponentsById) {

        return this._offersService.getOfferComponentsById(payload.parentId).pipe(
            catchError((error) => {
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: error.error.message.message,
                    position: 'toast-top-center'
                }));
                throw error;
            }),
            tap((offerComponents: OfferComponent[]) => {
                ctx.dispatch(new OnSetSharedGridRowData({
                    reload: true,
                    payload: offerComponents,
                    gridUUID: payload.gridUUID
                }));
            })
        );
    }

    @Selector()
    static getOfferComponents(state: OffersStateModel) {
        return state.offerComponents;
    }



    @Selector()
    static getCurrentRendererId(state: OffersStateModel) {
        return state.currentRendererId;
    }
}
